/**
 * Theming props for the Campaign Detail Widget.
 * These map to camelCase query params consumed by the iframe page.
 */
export interface CampaignDetailThemingProps {
  /** Container background color */
  detailBackgroundColor?: string;
  /** Primary text color */
  detailTextColor?: string;
  /** Secondary text color */
  detailSecondaryTextColor?: string;
  /** Border color */
  detailBorderColor?: string;
  /** Container border radius, e.g. "12px" */
  detailBorderRadius?: string;
  /** Container padding */
  detailPadding?: string;
  /** Container box shadow */
  detailShadow?: string;
  /** Primary accent color (CTAs, progress, links) */
  detailPrimaryColor?: string;
  /** Primary hover color */
  detailPrimaryHover?: string;
  /** Hero overlay color */
  detailHeroOverlay?: string;
  /** Reward badge background */
  detailRewardBg?: string;
  /** Reward badge text color */
  detailRewardColor?: string;
  /** Task complete state color */
  detailTaskCompleteColor?: string;
  /** Title font size, e.g. "1.5rem" */
  detailTitleSize?: string;
  /** Title font weight */
  detailTitleWeight?: string;
  /** Body text font size */
  detailBodySize?: string;
  /** Font family */
  detailFontFamily?: string;
  /** Button border radius */
  detailButtonRadius?: string;
  /** Transition duration */
  detailTransitionDuration?: string;
}

/**
 * Core configuration props for the Campaign Detail Widget.
 */
export interface CampaignDetailWidgetProps extends CampaignDetailThemingProps {
  /** Campaign UUID (required) */
  id: string;
  /** Base URL of the Legion application (required) */
  baseUrl: string;
  /** API key / bearer token for authenticated mode */
  apiKey?: string;
  /** Theme preset. Default: "light" */
  theme?: "light" | "dark";
  /** Widget height CSS value. Default: "auto" */
  height?: string | number;
  /** Widget width CSS value. Default: "100%" */
  width?: string;
  /** Additional CSS class name for the container */
  className?: string;
  /** Additional inline styles for the container */
  style?: React.CSSProperties;
  /** Callback when the widget iframe has loaded */
  onLoad?: (detail: { widget: string; id: string; theme: string }) => void;
  /** Callback when the widget iframe fails to load */
  onError?: (message: string) => void;
  /**
   * Callback fired when the embedded view requests to close
   * (user clicks the in-widget close affordance, or completes the flow).
   * Hosts typically use this to close the modal that wraps the widget.
   */
  onClose?: () => void;
  /**
   * Callback fired when the user successfully claims / begins the campaign.
   * Hosts can use this to refetch their campaign listing.
   */
  onClaim?: (detail: { id: string }) => void;
  /**
   * Callback fired when the embedded view requires the user to sign in
   * (e.g. they click "Claim Gift" / "Start Earning" without a valid token).
   * Hosts typically use this to open their own login UI, then push a fresh
   * `auth-token` back to the widget.
   */
  onRequestLogin?: (detail: { id: string | null }) => void;
}

/**
 * Mapping from camelCase theming prop names to the kebab-case query
 * parameter names used by the campaign detail widget iframe URL.
 */
export const CAMPAIGN_DETAIL_THEMING_PROP_TO_PARAM: Record<
  keyof CampaignDetailThemingProps,
  string
> = {
  detailBackgroundColor: "detail-background-color",
  detailTextColor: "detail-text-color",
  detailSecondaryTextColor: "detail-secondary-text-color",
  detailBorderColor: "detail-border-color",
  detailBorderRadius: "detail-border-radius",
  detailPadding: "detail-padding",
  detailShadow: "detail-shadow",
  detailPrimaryColor: "detail-primary-color",
  detailPrimaryHover: "detail-primary-hover",
  detailHeroOverlay: "detail-hero-overlay",
  detailRewardBg: "detail-reward-bg",
  detailRewardColor: "detail-reward-color",
  detailTaskCompleteColor: "detail-task-complete-color",
  detailTitleSize: "detail-title-size",
  detailTitleWeight: "detail-title-weight",
  detailBodySize: "detail-body-size",
  detailFontFamily: "detail-font-family",
  detailButtonRadius: "detail-button-radius",
  detailTransitionDuration: "detail-transition-duration",
};
